import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnInit, Output, output } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { JobFilterFormDataSchema } from '@app/schemas/job-filter';
import { JOB_FILTER_DEFAULT_OPTIONS, JOB_LOCATION, JOB_SORT_BY_OPTIONS, JOB_TYPE } from '@app/utils/constants';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { JobListSchema, JobUserContextSchema } from '@app/schemas/jobs';
import { Store } from '@ngrx/store';
import { UnderscoreToLowerPipe } from '@app/utils/string-transform.pipe';
import { RouterLink } from '@angular/router';
import { EDUCATION_LEVELS } from '@app/utils/find-job';
import { UserBiqData } from '@app/schemas/biq';
import { CareerGoalDialogComponent } from '@app/shared/results/career-goal-dialog/career-goal-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { analytics } from '@app/events/segment/analytics';

import { MatRadioModule } from '@angular/material/radio';

@Component({
  selector: 'app-job-filter',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    UnderscoreToLowerPipe,
    RouterLink,
    MatRadioModule,
  ],
  templateUrl: './job-filter.component.html',
  styleUrl: './job-filter.component.css'
})
export class JobFilterComponent implements OnInit {
  @Output() filterFormEmitter = new EventEmitter<JobFilterFormDataSchema>();
  @Output() sortEmitter = new EventEmitter<string>();
  @Output() filterTabEmitter = new EventEmitter<boolean>();

  is_related_to_career_goal: boolean = false;
  formbuilder = inject(FormBuilder);
  showFilterOptions = false;
  sortByOptions = JOB_SORT_BY_OPTIONS;
  sortSelected = "match_score_category";

  jobTypes = JOB_TYPE;
  jobLocation = JOB_LOCATION;
  jobEducationLevels: { key: string; label: string }[] = EDUCATION_LEVELS;

  selectedType: string[] = JOB_FILTER_DEFAULT_OPTIONS.role;
  selectedLocation: string[] = JOB_FILTER_DEFAULT_OPTIONS.location;
  selectedEducation: string = JOB_FILTER_DEFAULT_OPTIONS.education;
  educationIcon: string = 'education-fill-1.svg';
  public filterBadgeCount: number = 0;
  is_bookmarked: boolean = false;
  career_goal: string = '';
  matchRankTooltip = `Your match quality score
        shows how well a career fits
        you based on your interests, skills,
        experiences and patterns from millions of 
        real-world career paths and job data.`;

  public jobListData!: JobListSchema[];
  public userFilterOptions!: JobFilterFormDataSchema;
  public userData!: UserBiqData;
  public userSortByOption!: string; 
  public isInternship: boolean = false;
  public userContext!: JobUserContextSchema | {};
  public userCareerName: string = '';

  readonly itemDialog = inject(MatDialog);
  readonly careerGoalDialogWidth = '650px';

  constructor(private store: Store<{ 
    jobList: JobListSchema[], 
    jobFilter: JobFilterFormDataSchema,
    userBiq: UserBiqData,
    jobSortedBy: string,
    careerGoal: string,
    jobUserContext: JobUserContextSchema | {},
  }>) {
    this.store.select('jobList').subscribe(value => this.jobListData = value);
    this.store.select('jobFilter').subscribe(value => this.userFilterOptions = value);
    this.store.select('userBiq').subscribe(value => this.userData = value);
    this.store.select('jobSortedBy').subscribe(value => this.userSortByOption = value);
    this.store.select('careerGoal').subscribe(value => {
      this.career_goal = value;
      this.is_related_to_career_goal = value ? true : false;
    });
    this.store.select('jobUserContext').subscribe(value => this.userContext = value);
  }

  ngOnInit() {
    this.sortSelected = this.userSortByOption;
    this.selectedType = this.userFilterOptions.role;
    if (this.userFilterOptions.role.includes('INTERNSHIP')) {
      this.isInternship = true;
    }
    this.selectedLocation = this.userFilterOptions.location;
    this.selectedEducation = this.userFilterOptions.education;
    this.is_bookmarked = this.userFilterOptions.is_bookmarked;
    this.is_related_to_career_goal = this.userFilterOptions.is_related_to_career_goal;
    this.setEducationLevel();
    this.countSelectedFilters();
    this.userCareerName = (this.userContext as JobUserContextSchema)?.careerGoal?.careerName || '';
  }

  toggleFilterOptions() {
    this.showFilterOptions = !this.showFilterOptions;
    this.filterTabEmitter.emit(this.showFilterOptions);
  }

  selectType(type: string) {
    if (this.selectedType.includes(type)) {
      const data = this.selectedType.filter(item => item !== type);
      this.selectedType = data.length === 0 ? this.selectedType : data;
    } else {
      const data = [...this.selectedType];
      data.push(type);
      this.selectedType = data;
    }
  }

  selectLocation(location: string) {
    /**
     * Check if there is atlest one location selected, 
     * if not, do not allow user to deselect the last location
     */
    if (this.selectedLocation.includes(location)) {
      const data = this.selectedLocation.filter(item => item !== location);
      this.selectedLocation = data.length === 0 ? this.selectedLocation : data;
    } else {
      const data = [...this.selectedLocation];
      data.push(location);
      this.selectedLocation = data;
    }
  }

  sortResults(event: any) {
    analytics.track('Job Filter Sort', {
      section: 'job',
      sort: event.target.value,
    });
    this.sortEmitter.emit(event.target.value);
  }

  clearFilters() {
    this.selectedType = JOB_FILTER_DEFAULT_OPTIONS.role;
    this.selectedLocation = JOB_FILTER_DEFAULT_OPTIONS.location;
    this.selectedEducation = JOB_FILTER_DEFAULT_OPTIONS.education;
    this.is_bookmarked = JOB_FILTER_DEFAULT_OPTIONS.is_bookmarked;
    this.is_related_to_career_goal = this.career_goal ? true : JOB_FILTER_DEFAULT_OPTIONS.is_related_to_career_goal;
    this.filterBadgeCount = 0;
    this.isInternship = false;
  }

  applyFilters() {
    this.toggleFilterOptions();
    this.countSelectedFilters();
    this.filterFormEmitter.emit({
      role: this.isInternship ? ['INTERNSHIP'] : this.selectedType,
      location: this.selectedLocation,
      education: this.selectedEducation,
      is_bookmarked: this.is_bookmarked,
      is_related_to_career_goal: this.is_related_to_career_goal,
    });
  }

  countSelectedFilters() {
    let count = 0;
    const sortedSelectedType = [...this.selectedType].sort();
    const sortedDefaultType = [...JOB_FILTER_DEFAULT_OPTIONS.role].sort();
    if (JSON.stringify(sortedSelectedType) !== JSON.stringify(sortedDefaultType)) count++;
    if (JSON.stringify(this.selectedLocation) !== JSON.stringify(JOB_FILTER_DEFAULT_OPTIONS.location)) count++;
    if (this.selectedEducation !== JOB_FILTER_DEFAULT_OPTIONS.education) count++;
    if (this.is_bookmarked !== JOB_FILTER_DEFAULT_OPTIONS.is_bookmarked) count++;
    if (this.career_goal && !this.is_related_to_career_goal) count++;
    this.filterBadgeCount = count;
  }

  onSortArrowKeydown(event: KeyboardEvent, selectRef: any) {
    if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
      event.preventDefault();
      if (selectRef && typeof selectRef.openPanel === 'function') {
        selectRef.openPanel();
      }
    }
  }

  setEducationLevel(level: string = this.selectedEducation) {
    this.selectedEducation = level;
  }

  educationLevelIndex() {
    return this.jobEducationLevels.findIndex(item => item.key === this.selectedEducation);
  }

  educationLevelLabel() {
    const level = this.jobEducationLevels.find(item => item.key === this.selectedEducation);
    return level ? level.label : '';
  }

  navigateToCareerGoal() {
    const ref = this.itemDialog.open(CareerGoalDialogComponent, {
      minWidth: this.careerGoalDialogWidth,
      maxWidth: this.careerGoalDialogWidth,
      width: this.careerGoalDialogWidth,
    });

    ref.componentInstance.showCloseIcon = true;

    analytics.track('Job Filter Goal', {
      section: 'job',
      current_goal: this.userCareerName || null
    });
  }
}
